import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RouterLink } from '../../utils/linkNav';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Avatar,
  Tabs,
  Tab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Snackbar,
  Alert,
  Skeleton,
  Chip,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Add,
  Delete,
  Visibility,
  VisibilityOff,
  Business,
  LocationOn,
  SupervisorAccount,
  EventAvailable,
  AccountBalanceWallet,
  Groups,
  Description,
  SwapHoriz,
  Flag,
  Star,
  School,
  Devices,
  AccountBalance,
  History,
  BeachAccess,
  ReceiptLong,
  PersonAdd,
  WorkHistory,
  RateReview,
  MilitaryTech,
} from '@mui/icons-material';

import hrService, { Department, Designation, Employee, EmployeeListItem, EmployeeStatus } from '../../services/hrService';
import hrPlusService from '../../services/hrPlusService';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';

// ---------------------------------------------------------------------------
// Employee 360 — flagship HCM identity view. Merges the /hr-command 360
// aggregation (attendance/leave/payroll/salary/counts/timeline) with the
// per-domain lists (documents, movements, goals, skills, trainings, assets,
// loans) served by hrPlusService. The API mixes camelCase (360 payload) with
// snake_case (raw table rows, e.g. movements/documents), so every read goes
// through `pick()` rather than assuming one casing — never invent a value.
// ---------------------------------------------------------------------------

const TAB_LABELS = [
  'Overview',
  'Personal',
  'Employment',
  'Attendance',
  'Leave',
  'Payroll',
  'Salary',
  'Documents',
  'Movements',
  'Goals',
  'Skills',
  'Trainings',
  'Assets',
  'Loans',
  'Timeline',
];

const STATUS_TONE: Record<EmployeeStatus, StatusTone> = {
  ACTIVE: 'success',
  ON_LEAVE: 'info',
  NOTICE: 'warning',
  EXITED: 'error',
  SUSPENDED: 'neutral',
};

const STATUS_LABEL: Record<EmployeeStatus, string> = {
  ACTIVE: 'Active',
  ON_LEAVE: 'On Leave',
  NOTICE: 'Notice Period',
  EXITED: 'Exited',
  SUSPENDED: 'Suspended',
};

/** Reads the first present key — payloads mix camelCase and snake_case per source. */
const pick = (obj: any, ...keys: string[]): any => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return undefined;
};

const asNum = (v: any): number | null => {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const money = (v: any): React.ReactNode => {
  const n = asNum(v);
  return n === null ? null : formatIndianNumber(n);
};

const fmtDate = (v: any): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (v: any): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-IN');
};

const tenureOf = (doj: any): string => {
  if (!doj) return '—';
  const start = new Date(doj);
  if (Number.isNaN(start.getTime())) return '—';
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years <= 0 && months <= 0) return '<1m';
  const parts: string[] = [];
  if (years > 0) parts.push(`${years}y`);
  parts.push(`${months}m`);
  return parts.join(' ');
};

const safeJson = (v: any): any => {
  if (v === null || v === undefined) return {};
  if (typeof v === 'object') return v;
  try {
    return JSON.parse(v);
  } catch {
    return {};
  }
};

/** Masks a PAN (ABCDE1234F) to its first two and last two characters. */
const maskPan = (pan: string): string => {
  if (pan.length <= 4) return '•'.repeat(pan.length);
  return `${pan.slice(0, 2)}${'•'.repeat(pan.length - 4)}${pan.slice(-2)}`;
};

/** Masks all but the last 4 characters — used for bank account numbers. */
const maskLast4 = (value: string): string => {
  const tail = value.slice(-4);
  return `${'•'.repeat(Math.max(value.length - 4, 4))}${tail}`;
};

/** Masked-by-default reveal for a single sensitive field. */
const RevealField: React.FC<{ label: string; value: string | null | undefined; mask: (v: string) => string }> = ({
  label,
  value,
  mask,
}) => {
  const [revealed, setRevealed] = useState(false);
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      {value ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {revealed ? value : mask(value)}
          </Typography>
          <IconButton size="small" onClick={() => setRevealed((r) => !r)} aria-label={revealed ? `Hide ${label}` : `Reveal ${label}`}>
            {revealed ? <VisibilityOff sx={{ fontSize: 15 }} /> : <Visibility sx={{ fontSize: 15 }} />}
          </IconButton>
        </Box>
      ) : (
        <Typography variant="body2">—</Typography>
      )}
    </Box>
  );
};

/** Plain labelled field for a details grid. */
const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
      {value === null || value === undefined || value === '' ? '—' : value}
    </Typography>
  </Box>
);

const SectionPaper: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  action,
  children,
}) => (
  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 2.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {action}
    </Stack>
    {children}
  </Paper>
);

const timelineMeta = (kind: string): { icon: React.ReactNode; color: string } => {
  switch (kind) {
    case 'joined':
      return { icon: <PersonAdd fontSize="small" />, color: '#0284c7' };
    case 'movement':
      return { icon: <SwapHoriz fontSize="small" />, color: '#7c3aed' };
    case 'leave':
      return { icon: <BeachAccess fontSize="small" />, color: '#059669' };
    case 'review':
      return { icon: <RateReview fontSize="small" />, color: '#db2777' };
    case 'training':
      return { icon: <School fontSize="small" />, color: '#d97706' };
    default:
      return { icon: <WorkHistory fontSize="small" />, color: '#64748b' };
  }
};

const MOVEMENT_TYPES = ['TRANSFER', 'PROMOTION', 'SALARY_REVISION', 'ROLE_CHANGE', 'CONFIRMATION'];

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const Employee360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const employeeId = Number(id);
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Per-domain lists — fetched alongside the base 360 payload, each guarded
  // independently so one failing endpoint never blanks the whole page.
  const [documents, setDocuments] = useState<any[]>([]);
  const [movements, setMovements] = useState<any[]>([]);
  const [goals, setGoals] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [trainings, setTrainings] = useState<any[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [loans, setLoans] = useState<any[]>([]);
  const [salary, setSalary] = useState<{ current: any; history: any[] } | null>(null);

  // Lookups for dialogs (movements: transfer target dept/designation/manager)
  const [departments, setDepartments] = useState<Department[]>([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [managers, setManagers] = useState<EmployeeListItem[]>([]);

  const load = useCallback(async () => {
    if (!employeeId || Number.isNaN(employeeId)) return;
    setLoading(true);
    setError(null);
    try {
      const d = await hrPlusService.employee360(employeeId);
      setData(d);
    } catch (e: any) {
      setError(e.message || 'Failed to load employee 360');
      setLoading(false);
      return;
    }
    setLoading(false);

    // Best-effort secondary loads — never block the header/identity band.
    hrPlusService.getDocuments(employeeId).then((r) => setDocuments(Array.isArray(r?.documents) ? r.documents : [])).catch(() => undefined);
    hrPlusService.getMovements(employeeId).then((r) => setMovements(Array.isArray(r?.movements) ? r.movements : [])).catch(() => undefined);
    hrPlusService.employeeGoals(employeeId).then((r) => setGoals(Array.isArray(r?.goals) ? r.goals : [])).catch(() => undefined);
    hrPlusService.employeeSkills(employeeId).then((r) => setSkills(Array.isArray(r?.skills) ? r.skills : [])).catch(() => undefined);
    hrPlusService.employeeTrainings(employeeId).then((r) => setTrainings(Array.isArray(r?.trainings) ? r.trainings : [])).catch(() => undefined);
    hrPlusService.employeeAssets(employeeId).then((r) => setAssets(Array.isArray(r?.assets) ? r.assets : [])).catch(() => undefined);
    hrPlusService.employeeLoans(employeeId).then((r) => setLoans(Array.isArray(r?.loans) ? r.loans : [])).catch(() => undefined);
    hrPlusService.employeeSalary(employeeId).then((r) => setSalary(r || null)).catch(() => undefined);
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    Promise.all([hrService.getDepartments(), hrService.getDesignations(), hrService.getEmployees({ limit: 200 })])
      .then(([d, ds, e]) => {
        setDepartments(Array.isArray(d.departments) ? d.departments : []);
        setDesignations(Array.isArray(ds.designations) ? ds.designations : []);
        setManagers(Array.isArray(e.employees) ? e.employees : []);
      })
      .catch(() => undefined);
  }, []);

  const refreshDocuments = useCallback(() => {
    hrPlusService.getDocuments(employeeId).then((r) => setDocuments(Array.isArray(r?.documents) ? r.documents : [])).catch(() => undefined);
  }, [employeeId]);
  const refreshMovements = useCallback(() => {
    hrPlusService.getMovements(employeeId).then((r) => setMovements(Array.isArray(r?.movements) ? r.movements : [])).catch(() => undefined);
  }, [employeeId]);
  const refreshSkills = useCallback(() => {
    hrPlusService.employeeSkills(employeeId).then((r) => setSkills(Array.isArray(r?.skills) ? r.skills : [])).catch(() => undefined);
  }, [employeeId]);
  const refreshSalary = useCallback(() => {
    hrPlusService.employeeSalary(employeeId).then((r) => setSalary(r || null)).catch(() => undefined);
  }, [employeeId]);

  // ---- Dialog state ----
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docForm, setDocForm] = useState({ fileName: '', url: '', docType: '', docCategory: '', issueDate: '', expiryDate: '' });
  const [docSaving, setDocSaving] = useState(false);
  const [docDeleteTarget, setDocDeleteTarget] = useState<any>(null);

  const [movDialogOpen, setMovDialogOpen] = useState(false);
  const [movForm, setMovForm] = useState({
    movementType: 'TRANSFER',
    effectiveDate: '',
    toDepartmentId: '',
    toDesignationId: '',
    toManagerId: '',
    toGrade: '',
    toCtcAnnual: '',
    reason: '',
  });
  const [movSaving, setMovSaving] = useState(false);
  const [applyingMovId, setApplyingMovId] = useState<number | null>(null);

  const [skillDialogOpen, setSkillDialogOpen] = useState(false);
  const [skillForm, setSkillForm] = useState({ skillName: '', category: '', currentLevel: '', targetLevel: '' });
  const [skillSaving, setSkillSaving] = useState(false);

  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionForm, setRevisionForm] = useState({ ctcAnnual: '', effectiveFrom: '', revisionReason: '' });
  const [revisionSaving, setRevisionSaving] = useState(false);

  // ---- Derived, defensively-guarded views ----
  const employee: Partial<Employee> & Record<string, any> = data?.employee || {};
  const attendanceSummary = data?.attendanceSummary || null;
  const leaveBalances: any[] = Array.isArray(data?.leaveBalances) ? data.leaveBalances : [];
  const latestPayslip = data?.latestPayslip || null;
  const currentSalary = data?.currentSalary || null;
  const counts = data?.counts || {};
  const timeline: any[] = Array.isArray(data?.timeline) ? data.timeline : [];

  const fullName = [employee.title, employee.first_name, employee.middle_name, employee.last_name].filter(Boolean).join(' ') || 'Unnamed Employee';
  const status = (employee.status as EmployeeStatus) || 'ACTIVE';
  const effectiveCtc = asNum(pick(salary?.current, 'ctc_annual')) ?? asNum(currentSalary?.ctcAnnual) ?? asNum(employee.ctc_annual);

  const attendancePct = useMemo(() => {
    if (!attendanceSummary) return null;
    const working = (attendanceSummary.present || 0) + (attendanceSummary.absent || 0) + (attendanceSummary.halfDay || 0) + (attendanceSummary.onLeave || 0) + (attendanceSummary.wfh || 0);
    if (working <= 0) return null;
    return Math.round(((attendanceSummary.present + attendanceSummary.wfh) / working) * 1000) / 10;
  }, [attendanceSummary]);

  const leaveRemaining = useMemo(() => {
    if (leaveBalances.length === 0) return null;
    return leaveBalances.reduce((sum, b) => sum + (asNum(b.entitled) ?? 0) - (asNum(b.taken) ?? 0) - (asNum(b.pending) ?? 0), 0);
  }, [leaveBalances]);

  const openGoals = counts?.goals ?? null;

  const initials = `${(employee.first_name || '?').charAt(0)}${(employee.last_name || '').charAt(0)}`.toUpperCase();

  const documentExpiryFlag = (expiryDate: any): { label: string; tone: StatusTone } | null => {
    if (!expiryDate) return null;
    const d = new Date(expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const in30 = new Date(today);
    in30.setDate(in30.getDate() + 30);
    if (d < today) return { label: 'Expired', tone: 'error' };
    if (d <= in30) return { label: 'Expiring Soon', tone: 'warning' };
    return null;
  };

  // ---- Handlers ----

  const handleAddDocument = async () => {
    if (!docForm.fileName.trim()) {
      setToast({ msg: 'File name is required', severity: 'error' });
      return;
    }
    setDocSaving(true);
    try {
      await hrPlusService.addDocument(employeeId, {
        fileName: docForm.fileName.trim(),
        url: docForm.url.trim() || undefined,
        docType: docForm.docType.trim() || undefined,
        docCategory: docForm.docCategory.trim() || undefined,
        issueDate: docForm.issueDate || undefined,
        expiryDate: docForm.expiryDate || undefined,
      });
      setToast({ msg: 'Document added', severity: 'success' });
      setDocDialogOpen(false);
      setDocForm({ fileName: '', url: '', docType: '', docCategory: '', issueDate: '', expiryDate: '' });
      refreshDocuments();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to add document', severity: 'error' });
    } finally {
      setDocSaving(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!docDeleteTarget) return;
    try {
      await hrPlusService.deleteDocument(docDeleteTarget.id);
      setToast({ msg: 'Document removed', severity: 'success' });
      setDocDeleteTarget(null);
      refreshDocuments();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to remove document', severity: 'error' });
    }
  };

  const handleCreateMovement = async () => {
    if (!movForm.effectiveDate) {
      setToast({ msg: 'Effective date is required', severity: 'error' });
      return;
    }
    setMovSaving(true);
    try {
      await hrPlusService.createMovement(employeeId, {
        movementType: movForm.movementType,
        effectiveDate: movForm.effectiveDate,
        toDepartmentId: movForm.toDepartmentId ? Number(movForm.toDepartmentId) : undefined,
        toDesignationId: movForm.toDesignationId ? Number(movForm.toDesignationId) : undefined,
        toManagerId: movForm.toManagerId ? Number(movForm.toManagerId) : undefined,
        toGrade: movForm.toGrade.trim() || undefined,
        toCtcAnnual: movForm.toCtcAnnual ? Number(movForm.toCtcAnnual) : undefined,
        reason: movForm.reason.trim() || undefined,
      });
      setToast({ msg: 'Movement submitted', severity: 'success' });
      setMovDialogOpen(false);
      setMovForm({ movementType: 'TRANSFER', effectiveDate: '', toDepartmentId: '', toDesignationId: '', toManagerId: '', toGrade: '', toCtcAnnual: '', reason: '' });
      refreshMovements();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to submit movement', severity: 'error' });
    } finally {
      setMovSaving(false);
    }
  };

  const handleApplyMovement = async (movId: number) => {
    setApplyingMovId(movId);
    try {
      await hrPlusService.applyMovement(movId);
      setToast({ msg: 'Movement applied', severity: 'success' });
      refreshMovements();
      load();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to apply movement', severity: 'error' });
    } finally {
      setApplyingMovId(null);
    }
  };

  const handleAddSkill = async () => {
    if (!skillForm.skillName.trim()) {
      setToast({ msg: 'Skill name is required', severity: 'error' });
      return;
    }
    setSkillSaving(true);
    try {
      await hrPlusService.addSkill(employeeId, {
        skillName: skillForm.skillName.trim(),
        category: skillForm.category.trim() || undefined,
        currentLevel: skillForm.currentLevel ? Number(skillForm.currentLevel) : undefined,
        targetLevel: skillForm.targetLevel ? Number(skillForm.targetLevel) : undefined,
      });
      setToast({ msg: 'Skill added', severity: 'success' });
      setSkillDialogOpen(false);
      setSkillForm({ skillName: '', category: '', currentLevel: '', targetLevel: '' });
      refreshSkills();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to add skill', severity: 'error' });
    } finally {
      setSkillSaving(false);
    }
  };

  const handleReviseSalary = async () => {
    const ctc = Number(revisionForm.ctcAnnual);
    if (!ctc || ctc <= 0 || !revisionForm.effectiveFrom) {
      setToast({ msg: 'CTC and effective date are required', severity: 'error' });
      return;
    }
    setRevisionSaving(true);
    try {
      if (typeof (hrPlusService as any).reviseSalary === 'function') {
        await (hrPlusService as any).reviseSalary(employeeId, {
          ctcAnnual: ctc,
          effectiveFrom: revisionForm.effectiveFrom,
          revisionReason: revisionForm.revisionReason.trim() || undefined,
        });
        setToast({ msg: 'Salary revision recorded', severity: 'success' });
        setRevisionDialogOpen(false);
        setRevisionForm({ ctcAnnual: '', effectiveFrom: '', revisionReason: '' });
        refreshSalary();
        load();
      }
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to record salary revision', severity: 'error' });
    } finally {
      setRevisionSaving(false);
    }
  };

  // ---- States ----

  if (loading && !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={160} height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={110} sx={{ mb: 2.5, borderRadius: 3 }} />
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid key={i} size={{ xs: 6, md: 3 }}>
              <Skeleton variant="rounded" height={92} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={44} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (error || !data || !employee.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/hr/employees')} sx={{ mb: 1 }}>
          Back to Employees
        </Button>
        <TableSurface>
          <ErrorState message={error || 'Employee not found'} onRetry={load} />
        </TableSurface>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/hr/employees')} sx={{ mb: 1 }}>
        Back to Employees
      </Button>

      <PageHeader
        title={fullName}
        qualifier={employee.employee_code ? `(${employee.employee_code})` : undefined}
        subtitle={[employee.designation_title, employee.dept_name, employee.location_name || employee.work_location].filter(Boolean).join('  ·  ')}
        actions={
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
            <StatusDot label={STATUS_LABEL[status] || status} tone={STATUS_TONE[status] || 'neutral'} />
            <Button variant="outlined" size="small" startIcon={<Edit />} component={RouterLink} to={`/hr/employees/${employeeId}/edit`}>
              Edit Employee
            </Button>
          </Stack>
        }
      />

      {/* Identity strip */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2.5} alignItems="center">
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <Avatar
              src={employee.photo_url || undefined}
              sx={{ width: 64, height: 64, fontSize: '1.5rem', bgcolor: 'primary.main' }}
            >
              {initials || '?'}
            </Avatar>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Field label="Manager" value={employee.manager_id
              ? <Box component="span" onClick={() => navigate(`/hr/employees/${employee.manager_id}/360`)} sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>{employee.manager_name || `#${employee.manager_id}`}</Box>
              : employee.manager_name} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Field label="Employment Type" value={employee.employment_type ? String(employee.employment_type).replace('_', ' ') : undefined} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Field label="Joined" value={fmtDate(employee.date_of_joining)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Field label="Tenure" value={tenureOf(employee.date_of_joining)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Field
              label="Location"
              value={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <LocationOn sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <span>{employee.location_name || employee.work_location || '—'}</span>
                </Stack>
              }
            />
          </Grid>
        </Grid>
      </Paper>

      {/* KPI row */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="CTC (Annual)" value={money(effectiveCtc)} icon={<AccountBalanceWallet />} color="#2563eb" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="Attendance (30d)" value={attendancePct === null ? null : `${attendancePct}%`} icon={<EventAvailable />} color="#059669" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="Leave Balance" value={leaveRemaining === null ? null : `${leaveRemaining} days`} icon={<BeachAccess />} color="#7c3aed" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="Open Goals" value={openGoals} icon={<Flag />} color="#d97706" />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}
      >
        {TAB_LABELS.map((t) => (
          <Tab key={t} label={t} />
        ))}
      </Tabs>

      {/* ---------------- Overview ---------------- */}
      {tab === 0 && (
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, lg: 5 }}>
            <SectionPaper title="Key Facts">
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Field label="Department" value={employee.dept_name} />
                </Grid>
                <Grid size={6}>
                  <Field label="Designation" value={employee.designation_title} />
                </Grid>
                <Grid size={6}>
                  <Field label="Grade" value={employee.grade} />
                </Grid>
                <Grid size={6}>
                  <Field label="Business Unit" value={employee.business_unit} />
                </Grid>
                <Grid size={6}>
                  <Field label="Cost Center" value={employee.cost_center_name} />
                </Grid>
                <Grid size={6}>
                  <Field label="Email" value={employee.email} />
                </Grid>
              </Grid>
            </SectionPaper>
          </Grid>
          <Grid size={{ xs: 12, lg: 7 }}>
            <SectionPaper title="Activity Timeline">
              {timeline.length === 0 ? (
                <TableEmptyState title="No activity yet" hint="Joining, movements, leave, reviews and training completions will appear here." />
              ) : (
                <TimelineList entries={timeline.slice(0, 8)} />
              )}
              {timeline.length > 8 && (
                <Button size="small" onClick={() => setTab(14)} sx={{ mt: 1 }}>
                  View full timeline
                </Button>
              )}
            </SectionPaper>
          </Grid>
        </Grid>
      )}

      {/* ---------------- Personal ---------------- */}
      {tab === 1 && (
        <SectionPaper title="Personal Details">
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Field label="Gender" value={employee.gender} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Field label="Marital Status" value={employee.marital_status} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Field label="Blood Group" value={employee.blood_group} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Field label="Nationality" value={employee.nationality} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Field label="Date of Birth" value={fmtDate(employee.date_of_birth)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Field label="Personal Email" value={employee.personal_email} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Field label="Phone" value={employee.phone} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Field label="Alternate Phone" value={employee.alternate_phone} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }} />
            <Grid size={{ xs: 12, sm: 6 }}>
              <Field label="Current Address" value={employee.current_address} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Field label="Permanent Address" value={employee.permanent_address} />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Emergency Contact
          </Typography>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Field label="Name" value={employee.emergency_contact_name} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Field label="Phone" value={employee.emergency_contact_phone} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Field label="Relation" value={employee.emergency_contact_relation} />
            </Grid>
          </Grid>
        </SectionPaper>
      )}

      {/* ---------------- Employment (incl. statutory) ---------------- */}
      {tab === 2 && (
        <Box>
          <SectionPaper title="Employment">
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Employment Type" value={employee.employment_type ? String(employee.employment_type).replace('_', ' ') : undefined} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Date of Joining" value={fmtDate(employee.date_of_joining)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Date of Leaving" value={employee.date_of_leaving ? fmtDate(employee.date_of_leaving) : undefined} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Probation (months)" value={employee.probation_months} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Confirmation Date" value={employee.confirmation_date ? fmtDate(employee.confirmation_date) : undefined} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Notice Period (days)" value={employee.notice_period_days} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Grade" value={employee.grade} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Position" value={employee.position_title} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Business Unit" value={employee.business_unit} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Cost Center" value={employee.cost_center_name} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Work Location" value={employee.location_name || employee.work_location} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Field label="Manager" value={employee.manager_id
              ? <Box component="span" onClick={() => navigate(`/hr/employees/${employee.manager_id}/360`)} sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>{employee.manager_name || `#${employee.manager_id}`}</Box>
              : employee.manager_name} />
              </Grid>
            </Grid>
          </SectionPaper>

          <SectionPaper title="Statutory & Banking">
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1.5 }}>
              Masked by default — click the eye icon to reveal.
            </Typography>
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <RevealField label="PAN" value={employee.pan} mask={maskPan} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="Aadhaar" value={employee.aadhaar_last4 ? `•••• •••• ${employee.aadhaar_last4}` : undefined} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <RevealField label="Bank Account" value={employee.bank_account} mask={maskLast4} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="IFSC" value={employee.bank_ifsc} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="PF Number" value={employee.pf_number} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="UAN" value={employee.uan} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="ESI Number" value={employee.esi_number} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="Tax Regime" value={employee.tax_regime} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="PF Applicable" value={employee.pf_applicable !== undefined ? (employee.pf_applicable ? 'Yes' : 'No') : undefined} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="ESI Applicable" value={employee.esi_applicable !== undefined ? (employee.esi_applicable ? 'Yes' : 'No') : undefined} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="PT Applicable" value={employee.pt_applicable !== undefined ? (employee.pt_applicable ? 'Yes' : 'No') : undefined} />
              </Grid>
            </Grid>
          </SectionPaper>
        </Box>
      )}

      {/* ---------------- Attendance ---------------- */}
      {tab === 3 && (
        <SectionPaper title="Attendance — Last 30 Days">
          {!attendanceSummary ? (
            <TableEmptyState title="No attendance recorded" hint="Attendance summaries appear once check-ins are logged." />
          ) : (
            <Grid container spacing={2}>
              {[
                ['Present', attendanceSummary.present],
                ['Absent', attendanceSummary.absent],
                ['Half Day', attendanceSummary.halfDay],
                ['On Leave', attendanceSummary.onLeave],
                ['WFH', attendanceSummary.wfh],
                ['Holiday', attendanceSummary.holiday],
                ['Week Off', attendanceSummary.weekOff],
                ['Avg. Worked Hours', attendanceSummary.avgWorkedHours !== null ? attendanceSummary.avgWorkedHours : undefined],
              ].map(([label, value]) => (
                <Grid key={label as string} size={{ xs: 6, sm: 3 }}>
                  <Field label={label as string} value={value as React.ReactNode} />
                </Grid>
              ))}
            </Grid>
          )}
        </SectionPaper>
      )}

      {/* ---------------- Leave ---------------- */}
      {tab === 4 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Leave Type</TableCell>
                  <TableCell sx={headerCellSx} align="right">Entitled</TableCell>
                  <TableCell sx={headerCellSx} align="right">Taken</TableCell>
                  <TableCell sx={headerCellSx} align="right">Pending</TableCell>
                  <TableCell sx={headerCellSx} align="right">Remaining</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaveBalances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ border: 0 }}>
                      <TableEmptyState title="No leave balances" hint="Leave balances appear once a financial year is configured for this employee." />
                    </TableCell>
                  </TableRow>
                ) : (
                  leaveBalances.map((b, i) => {
                    const entitled = asNum(b.entitled) ?? 0;
                    const taken = asNum(b.taken) ?? 0;
                    const pending = asNum(b.pending) ?? 0;
                    return (
                      <TableRow key={b.id ?? i} hover sx={bodyRowSx}>
                        <TableCell sx={{ fontWeight: 600 }}>{b.leave_type_name || '—'}</TableCell>
                        <TableCell align="right">{entitled}</TableCell>
                        <TableCell align="right">{taken}</TableCell>
                        <TableCell align="right">{pending}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{entitled - taken - pending}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Payroll ---------------- */}
      {tab === 5 && (
        <SectionPaper title={latestPayslip ? `Latest Payslip — ${latestPayslip.run_code || `${latestPayslip.period_month}/${latestPayslip.period_year}`}` : 'Latest Payslip'}>
          {!latestPayslip ? (
            <TableEmptyState title="No payslip on record" hint="A payslip will appear here once a payroll run including this employee is processed." />
          ) : (
            <Grid container spacing={2}>
              {[
                ['Basic', money(latestPayslip.basic)],
                ['HRA', money(latestPayslip.hra)],
                ['Allowances', money(latestPayslip.allowances)],
                ['Overtime Pay', money(latestPayslip.overtime_pay)],
                ['Reimbursements', money(latestPayslip.reimbursements)],
                ['Gross', money(latestPayslip.gross)],
                ['PF (Employee)', money(latestPayslip.pf_employee)],
                ['ESI (Employee)', money(latestPayslip.esi_employee)],
                ['Professional Tax', money(latestPayslip.professional_tax)],
                ['TDS', money(latestPayslip.tds)],
                ['Loan Deduction', money(latestPayslip.loan_deduction)],
                ['Other Deductions', money(latestPayslip.other_deductions)],
                ['Net Pay', money(latestPayslip.net_pay)],
                ['Days Paid', latestPayslip.days_paid],
                ['LOP Days', latestPayslip.lop_days],
              ].map(([label, value]) => (
                <Grid key={label as string} size={{ xs: 6, sm: 4, md: 3 }}>
                  <Field label={label as string} value={value as React.ReactNode} />
                </Grid>
              ))}
            </Grid>
          )}
        </SectionPaper>
      )}

      {/* ---------------- Salary ---------------- */}
      {tab === 6 && (
        <Box>
          <SectionPaper
            title="Current Compensation"
            action={
              <Button size="small" variant="contained" startIcon={<Add />} onClick={() => setRevisionDialogOpen(true)}>
                Record Revision
              </Button>
            }
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Field label="CTC (Annual)" value={money(pick(salary?.current, 'ctc_annual')) ?? money(currentSalary?.ctcAnnual)} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Field label="Effective From" value={pick(salary?.current, 'effective_from') ? fmtDate(pick(salary?.current, 'effective_from')) : (currentSalary?.effectiveFrom ? fmtDate(currentSalary.effectiveFrom) : undefined)} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Field label="Salary Structure" value={pick(salary?.current, 'structure_name')} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Field label="Revision Reason" value={pick(salary?.current, 'revision_reason')} />
              </Grid>
            </Grid>
          </SectionPaper>

          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Effective From</TableCell>
                    <TableCell sx={headerCellSx} align="right">CTC (Annual)</TableCell>
                    <TableCell sx={headerCellSx}>Structure</TableCell>
                    <TableCell sx={headerCellSx}>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!salary || salary.history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ border: 0 }}>
                        <TableEmptyState title="No prior revisions" hint="Compensation history appears once a revision is recorded." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    salary.history.map((h: any, i: number) => (
                      <TableRow key={h.id ?? i} hover sx={bodyRowSx}>
                        <TableCell>{fmtDate(h.effective_from)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(h.ctc_annual)}</TableCell>
                        <TableCell>{h.structure_name || '—'}</TableCell>
                        <TableCell>{h.revision_reason || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Box>
      )}

      {/* ---------------- Documents ---------------- */}
      {tab === 7 && (
        <>
        <Box sx={{ mb: 2 }}>
          <AttachmentsPanel entityType="employee" entityId={employee.id} title="Employee Files" />
        </Box>
        <TableSurface>
          <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Documents
            </Typography>
            <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setDocDialogOpen(true)}>
              Add Document Link
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>File</TableCell>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx}>Category</TableCell>
                  <TableCell sx={headerCellSx}>Issue Date</TableCell>
                  <TableCell sx={headerCellSx}>Expiry Date</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={headerCellSx} />
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ border: 0 }}>
                      <TableEmptyState title="No documents on file" hint="Add a link to PAN, Aadhaar, offer letter or any other employee document." />
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((doc) => {
                    const expiry = documentExpiryFlag(doc.expiry_date);
                    return (
                      <TableRow key={doc.id} hover sx={bodyRowSx}>
                        <TableCell>
                          {doc.url ? (
                            <a href={doc.url} target="_blank" rel="noreferrer" style={{ color: 'inherit', fontWeight: 600 }}>
                              {doc.file_name}
                            </a>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{doc.file_name}</Typography>
                          )}
                        </TableCell>
                        <TableCell>{doc.doc_type || '—'}</TableCell>
                        <TableCell>{doc.doc_category || '—'}</TableCell>
                        <TableCell>{fmtDate(doc.issue_date)}</TableCell>
                        <TableCell>
                          {fmtDate(doc.expiry_date)}
                          {expiry && <Chip size="small" sx={{ ml: 1 }} color={expiry.tone === 'error' ? 'error' : 'warning'} label={expiry.label} />}
                        </TableCell>
                        <TableCell><StatusDot label={doc.status || 'ACTIVE'} tone={doc.status === 'ARCHIVED' ? 'neutral' : 'success'} /></TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => setDocDeleteTarget(doc)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
        </>
      )}

      {/* ---------------- Movements ---------------- */}
      {tab === 8 && (
        <TableSurface>
          <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Movements
            </Typography>
            <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setMovDialogOpen(true)}>
              New Movement
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx}>Effective Date</TableCell>
                  <TableCell sx={headerCellSx}>Reason</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={headerCellSx} />
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ border: 0 }}>
                      <TableEmptyState title="No movements yet" hint="Transfers, promotions, salary revisions and confirmations are logged here." />
                    </TableCell>
                  </TableRow>
                ) : (
                  movements.map((m) => (
                    <TableRow key={m.id} hover sx={bodyRowSx}>
                      <TableCell><TypePill label={String(m.movement_type || '—').replace(/_/g, ' ')} tint="#7c3aed" /></TableCell>
                      <TableCell>{fmtDate(m.effective_date)}</TableCell>
                      <TableCell>{m.reason || '—'}</TableCell>
                      <TableCell><StatusDot label={m.status || '—'} tone={m.status === 'APPLIED' ? 'success' : m.status === 'REJECTED' ? 'error' : 'neutral'} /></TableCell>
                      <TableCell align="right">
                        {m.status !== 'APPLIED' && m.status !== 'REJECTED' && (
                          <Button size="small" disabled={applyingMovId === m.id} onClick={() => handleApplyMovement(m.id)}>
                            {applyingMovId === m.id ? 'Applying…' : 'Apply'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Goals ---------------- */}
      {tab === 9 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Goal</TableCell>
                  <TableCell sx={headerCellSx}>Category</TableCell>
                  <TableCell sx={headerCellSx} align="right">Weight</TableCell>
                  <TableCell sx={headerCellSx} align="right">Progress</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {goals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ border: 0 }}>
                      <TableEmptyState title="No goals assigned" hint="Performance goals for the active cycle will appear here." />
                    </TableCell>
                  </TableRow>
                ) : (
                  goals.map((g) => (
                    <TableRow key={g.id} hover sx={bodyRowSx}>
                      <TableCell sx={{ fontWeight: 600 }}>{g.title}</TableCell>
                      <TableCell>{g.category || '—'}</TableCell>
                      <TableCell align="right">{g.weight !== null && g.weight !== undefined ? `${g.weight}%` : '—'}</TableCell>
                      <TableCell align="right" sx={{ minWidth: 120 }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                          <Box sx={{ width: 60 }}>
                            <LinearProgress variant="determinate" value={Math.min(Math.max(asNum(g.progress) ?? 0, 0), 100)} sx={{ borderRadius: 2, height: 6 }} />
                          </Box>
                          <Typography variant="caption">{asNum(g.progress) ?? 0}%</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell><StatusDot label={g.status || '—'} tone={g.status === 'ACHIEVED' ? 'success' : g.status === 'MISSED' ? 'error' : 'info'} /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Skills ---------------- */}
      {tab === 10 && (
        <TableSurface>
          <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Skills
            </Typography>
            <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setSkillDialogOpen(true)}>
              Add Skill
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Skill</TableCell>
                  <TableCell sx={headerCellSx}>Category</TableCell>
                  <TableCell sx={headerCellSx} align="right">Current Level</TableCell>
                  <TableCell sx={headerCellSx} align="right">Target Level</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skills.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ border: 0 }}>
                      <TableEmptyState title="No skills recorded" hint="Add skills to track competency against targets." />
                    </TableCell>
                  </TableRow>
                ) : (
                  skills.map((s) => (
                    <TableRow key={s.id} hover sx={bodyRowSx}>
                      <TableCell sx={{ fontWeight: 600 }}>{s.skill_name}</TableCell>
                      <TableCell>{s.category || '—'}</TableCell>
                      <TableCell align="right">{s.current_level ?? '—'}</TableCell>
                      <TableCell align="right">{s.target_level ?? '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Trainings ---------------- */}
      {tab === 11 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Training</TableCell>
                  <TableCell sx={headerCellSx}>Category</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={headerCellSx}>Completion Date</TableCell>
                  <TableCell sx={headerCellSx} align="right">Score</TableCell>
                  <TableCell sx={headerCellSx}>Certified</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trainings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ border: 0 }}>
                      <TableEmptyState title="No trainings enrolled" hint="Training enrollments and completions will appear here." />
                    </TableCell>
                  </TableRow>
                ) : (
                  trainings.map((t) => (
                    <TableRow key={t.id} hover sx={bodyRowSx}>
                      <TableCell sx={{ fontWeight: 600 }}>{t.training_name || '—'}</TableCell>
                      <TableCell>{t.category || '—'}</TableCell>
                      <TableCell><StatusDot label={t.status || '—'} tone={t.status === 'COMPLETED' ? 'success' : t.status === 'DROPPED' ? 'error' : 'info'} /></TableCell>
                      <TableCell>{t.completion_date ? fmtDate(t.completion_date) : '—'}</TableCell>
                      <TableCell align="right">{t.score ?? '—'}</TableCell>
                      <TableCell>{t.certified ? <MilitaryTech sx={{ fontSize: 18, color: '#d97706' }} /> : '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Assets ---------------- */}
      {tab === 12 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Asset</TableCell>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx}>Serial No.</TableCell>
                  <TableCell sx={headerCellSx}>Issued On</TableCell>
                  <TableCell sx={headerCellSx}>Returned On</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ border: 0 }}>
                      <TableEmptyState title="No assets issued" hint="Laptops, phones and other issued assets will appear here." />
                    </TableCell>
                  </TableRow>
                ) : (
                  assets.map((a) => (
                    <TableRow key={a.id} hover sx={bodyRowSx}>
                      <TableCell sx={{ fontWeight: 600 }}>{a.asset_name}</TableCell>
                      <TableCell>{a.asset_type || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{a.serial_no || '—'}</TableCell>
                      <TableCell>{a.issued_on ? fmtDate(a.issued_on) : '—'}</TableCell>
                      <TableCell>{a.returned_on ? fmtDate(a.returned_on) : '—'}</TableCell>
                      <TableCell><StatusDot label={a.status || '—'} tone={a.status === 'RETURNED' ? 'neutral' : a.status === 'LOST' || a.status === 'DAMAGED' ? 'error' : 'success'} /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Loans ---------------- */}
      {tab === 13 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx} align="right">Principal</TableCell>
                  <TableCell sx={headerCellSx} align="right">Outstanding</TableCell>
                  <TableCell sx={headerCellSx} align="right">EMI</TableCell>
                  <TableCell sx={headerCellSx}>Disbursed On</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ border: 0 }}>
                      <TableEmptyState title="No loans or advances" hint="Employee loans and salary advances will appear here." />
                    </TableCell>
                  </TableRow>
                ) : (
                  loans.map((l) => (
                    <TableRow key={l.id} hover sx={bodyRowSx}>
                      <TableCell><TypePill label={l.loan_type || '—'} tint="#0284c7" /></TableCell>
                      <TableCell align="right">{money(l.principal)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{money(l.outstanding)}</TableCell>
                      <TableCell align="right">{money(l.emi_amount)}</TableCell>
                      <TableCell>{l.disbursed_on ? fmtDate(l.disbursed_on) : '—'}</TableCell>
                      <TableCell><StatusDot label={l.status || '—'} tone={l.status === 'CLOSED' ? 'neutral' : l.status === 'ACTIVE' || l.status === 'APPROVED' ? 'success' : 'info'} /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Timeline ---------------- */}
      {tab === 14 && (
        <SectionPaper title="Activity Timeline">
          {timeline.length === 0 ? (
            <TableEmptyState title="No activity yet" hint="Joining, movements, leave, reviews and training completions will appear here." />
          ) : (
            <TimelineList entries={timeline} />
          )}
        </SectionPaper>
      )}

      {/* ================================================================ */}
      {/* Dialogs */}
      {/* ================================================================ */}

      {/* Add document */}
      <Dialog open={docDialogOpen} onClose={() => setDocDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Document Link</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField label="File Name" required fullWidth size="small" value={docForm.fileName} onChange={(e) => setDocForm((f) => ({ ...f, fileName: e.target.value }))} />
            <TextField label="URL" fullWidth size="small" value={docForm.url} onChange={(e) => setDocForm((f) => ({ ...f, url: e.target.value }))} />
            <TextField label="Document Type" fullWidth size="small" value={docForm.docType} onChange={(e) => setDocForm((f) => ({ ...f, docType: e.target.value }))} />
            <TextField label="Category" fullWidth size="small" value={docForm.docCategory} onChange={(e) => setDocForm((f) => ({ ...f, docCategory: e.target.value }))} />
            <TextField label="Issue Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={docForm.issueDate} onChange={(e) => setDocForm((f) => ({ ...f, issueDate: e.target.value }))} />
            <TextField label="Expiry Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={docForm.expiryDate} onChange={(e) => setDocForm((f) => ({ ...f, expiryDate: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDocDialogOpen(false)} disabled={docSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleAddDocument} disabled={docSaving}>{docSaving ? 'Saving…' : 'Add Document'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete document confirm */}
      <Dialog open={!!docDeleteTarget} onClose={() => setDocDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove Document</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Remove <b>{docDeleteTarget?.file_name}</b> from this employee's records?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDocDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteDocument}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* New movement */}
      <Dialog open={movDialogOpen} onClose={() => setMovDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Movement</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="Movement Type" fullWidth size="small" value={movForm.movementType} onChange={(e) => setMovForm((f) => ({ ...f, movementType: e.target.value }))}>
                {MOVEMENT_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Effective Date" type="date" required fullWidth size="small" InputLabelProps={{ shrink: true }} value={movForm.effectiveDate} onChange={(e) => setMovForm((f) => ({ ...f, effectiveDate: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="To Department" fullWidth size="small" value={movForm.toDepartmentId} onChange={(e) => setMovForm((f) => ({ ...f, toDepartmentId: e.target.value }))}>
                <MenuItem value="">— No change —</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.dept_name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="To Designation" fullWidth size="small" value={movForm.toDesignationId} onChange={(e) => setMovForm((f) => ({ ...f, toDesignationId: e.target.value }))}>
                <MenuItem value="">— No change —</MenuItem>
                {designations.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.title}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="To Manager" fullWidth size="small" value={movForm.toManagerId} onChange={(e) => setMovForm((f) => ({ ...f, toManagerId: e.target.value }))}>
                <MenuItem value="">— No change —</MenuItem>
                {managers.filter((m) => m.id !== employeeId).map((m) => (
                  <MenuItem key={m.id} value={m.id}>{[m.first_name, m.last_name].filter(Boolean).join(' ')} ({m.employee_code})</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="To Grade" fullWidth size="small" value={movForm.toGrade} onChange={(e) => setMovForm((f) => ({ ...f, toGrade: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="To CTC (Annual)" type="number" fullWidth size="small" value={movForm.toCtcAnnual} onChange={(e) => setMovForm((f) => ({ ...f, toCtcAnnual: e.target.value }))} />
            </Grid>
            <Grid size={12}>
              <TextField label="Reason" fullWidth size="small" multiline minRows={2} value={movForm.reason} onChange={(e) => setMovForm((f) => ({ ...f, reason: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setMovDialogOpen(false)} disabled={movSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateMovement} disabled={movSaving}>{movSaving ? 'Submitting…' : 'Submit Movement'}</Button>
        </DialogActions>
      </Dialog>

      {/* Add skill */}
      <Dialog open={skillDialogOpen} onClose={() => setSkillDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Skill</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField label="Skill Name" required fullWidth size="small" value={skillForm.skillName} onChange={(e) => setSkillForm((f) => ({ ...f, skillName: e.target.value }))} />
            <TextField label="Category" fullWidth size="small" value={skillForm.category} onChange={(e) => setSkillForm((f) => ({ ...f, category: e.target.value }))} />
            <TextField label="Current Level (1-5)" type="number" fullWidth size="small" value={skillForm.currentLevel} onChange={(e) => setSkillForm((f) => ({ ...f, currentLevel: e.target.value }))} />
            <TextField label="Target Level (1-5)" type="number" fullWidth size="small" value={skillForm.targetLevel} onChange={(e) => setSkillForm((f) => ({ ...f, targetLevel: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSkillDialogOpen(false)} disabled={skillSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSkill} disabled={skillSaving}>{skillSaving ? 'Saving…' : 'Add Skill'}</Button>
        </DialogActions>
      </Dialog>

      {/* Record salary revision */}
      <Dialog open={revisionDialogOpen} onClose={() => setRevisionDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Record Salary Revision</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField label="New CTC (Annual)" type="number" required fullWidth size="small" value={revisionForm.ctcAnnual} onChange={(e) => setRevisionForm((f) => ({ ...f, ctcAnnual: e.target.value }))} />
            <TextField label="Effective From" type="date" required fullWidth size="small" InputLabelProps={{ shrink: true }} value={revisionForm.effectiveFrom} onChange={(e) => setRevisionForm((f) => ({ ...f, effectiveFrom: e.target.value }))} />
            <TextField label="Revision Reason" fullWidth size="small" value={revisionForm.revisionReason} onChange={(e) => setRevisionForm((f) => ({ ...f, revisionReason: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRevisionDialogOpen(false)} disabled={revisionSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleReviseSalary} disabled={revisionSaving}>{revisionSaving ? 'Saving…' : 'Record Revision'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3200} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

/** Vertical chronological activity list — shared by the Overview preview and the full Timeline tab. */
const TimelineList: React.FC<{ entries: any[] }> = ({ entries }) => (
  <Box sx={{ position: 'relative', pl: 4 }}>
    <Box sx={{ position: 'absolute', left: 15, top: 6, bottom: 6, width: 2, bgcolor: 'divider' }} />
    <Stack spacing={2.5}>
      {entries.map((t, i) => {
        const kind = pick(t, 'kind', 'type') || 'event';
        const meta = timelineMeta(kind);
        return (
          <Box key={i} sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                left: -32,
                top: 0,
                width: 30,
                height: 30,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: `${meta.color}1a`,
                color: meta.color,
              }}
            >
              {meta.icon}
            </Box>
            <Box sx={{ p: 1.25, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.label}</Typography>
              <Typography variant="caption" color="text.secondary">{fmtDateTime(t.ts)}</Typography>
            </Box>
          </Box>
        );
      })}
    </Stack>
  </Box>
);

export default Employee360Page;
